import React, { useEffect, useState } from "react";
import {
  MdOutlineArrowForwardIos,
  MdOutlineArrowBackIos,
} from "react-icons/md";



export default function TransferTable({
  equipamentos,
  equipamentosDoGrupo,
  setLeftTableData,
  setRightTableData,
}) {
  const [selectedItemsLeft, setSelectedItemsLeft] = useState([]);
  const [selectedItemsRight, setSelectedItemsRight] = useState([]);
  const [selectAllLeft, setSelectAllLeft] = useState(false);
  const [selectAllRight, setSelectAllRight] = useState(false);

  const [leftTablePage, setLeftTablePage] = useState(1);
  const [rightTablePage, setRightTablePage] = useState(1);
  const itemsPerPage = 5;

  const [leftTableSearchTerm, setLeftTableSearchTerm] = useState("");
  const [rightTableSearchTerm, setRightTableSearchTerm] = useState("");

  // Dados mestres (completos) - separados dos dados de visualização
  const [masterLeftData, setMasterLeftData] = useState([]);
  const [masterRightData, setMasterRightData] = useState([]);

  // Dados filtrados para visualização
  const [filteredLeftData, setFilteredLeftData] = useState([]);
  const [filteredRightData, setFilteredRightData] = useState([]);

  const getComparableId = (item) => {
    if (item == null) return '';
    if (typeof item === 'string' || typeof item === 'number') return String(item);
    // tenta várias chaves comuns
    const candidate = item?.id_modelo ?? item?.modelo_id ?? item?.id ?? item?.idModelo ?? item?.idmodelo;
    if (candidate != null) return String(candidate);
    // fallback: se tiver nome, usa
    if (item?.nome) return String(item.nome);
    return '';
  };
  const isSame = (a, b) => getComparableId(a) === getComparableId(b);

  const buildIdsSetFromGrupo = (grupoData) => {
    const ids = [];
    if (!grupoData) return new Set();
    if (Array.isArray(grupoData)) {
      for (const it of grupoData) ids.push(getComparableId(it));
    } else if (typeof grupoData === 'string') {
      for (const part of grupoData.split(',')) {
        const v = String(part).trim();
        if (v) ids.push(v);
      }
    } else if (typeof grupoData === 'object' && typeof (grupoData.id_modelo ?? grupoData.modelo_id) === 'string') {
      const csv = String(grupoData.id_modelo ?? grupoData.modelo_id);
      for (const part of csv.split(',')) {
        const v = String(part).trim();
        if (v) ids.push(v);
      }
    } else if (typeof grupoData === 'object') {
      const v = getComparableId(grupoData);
      if (v) ids.push(v);
    }
    return new Set(ids);
  };

  const toggleSelect = (item, table) => {
    const setSelectedItems = table === "left" ? setSelectedItemsLeft : setSelectedItemsRight;
    setSelectedItems((prevSelected) => {
      const alreadySelected = prevSelected.some((i) => isSame(i, item));
      return alreadySelected
        ? prevSelected.filter((i) => !isSame(i, item))
        : [...prevSelected, item];
    });
  };

  const moveItems = (fromMasterData, toMasterData, setFromMasterData, setToMasterData, selectedItems) => {
    const removeFromTable = (fromMasterData || []).filter(
      (item) => !selectedItems.some((sel) => isSame(sel, item))
    );
    const merged = [...(toMasterData || []), ...(selectedItems || [])];
    const addToTable = merged.reduce((acc, curr) => {
      if (!acc.some((i) => isSame(i, curr))) acc.push(curr);
      return acc;
    }, []);

    setFromMasterData(removeFromTable);
    setToMasterData(addToTable);
  };

  const moveRight = () => {
    moveItems(masterLeftData, masterRightData, setMasterLeftData, setMasterRightData, selectedItemsLeft);
    setSelectedItemsLeft([]);
    setSelectAllLeft(false);
    setLeftTablePage(1); // Reset página
  };

  const moveLeft = () => {
    moveItems(masterRightData, masterLeftData, setMasterRightData, setMasterLeftData, selectedItemsRight);
    setSelectedItemsRight([]);
    setSelectAllRight(false);
    setRightTablePage(1); // Reset página
  };

  const handleCheckboxChange = (table) => {
    const setSelectAll =
      table === "left" ? setSelectAllLeft : setSelectAllRight;
    const setSelectedItems =
      table === "left" ? setSelectedItemsLeft : setSelectedItemsRight;
    const filteredData = table === "left" ? filteredLeftData : filteredRightData;
    const selectAll = table === "left" ? selectAllLeft : selectAllRight;

    setSelectAll(!selectAll);
    setSelectedItems(selectAll ? [] : filteredData || []);
  };

  const filterAndPaginateTable = (tableData, page, searchTerm) => {
    if (!tableData || !Array.isArray(tableData)) return [];

    const filteredData = tableData.filter((item) => {
      const itemName = String(item?.nome)?.toLowerCase() || '';
      const searchLower = String(searchTerm || '').toLowerCase();
      return itemName.includes(searchLower);
    });

    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    return filteredData.slice(startIndex, endIndex);
  };

  // Função para obter dados filtrados completos (sem paginação)
  const getFilteredData = (tableData, searchTerm) => {
    if (!tableData || !Array.isArray(tableData)) return [];

    return tableData.filter((item) => {
      const itemName = String(item?.nome)?.toLowerCase() || '';
      const searchLower = String(searchTerm || '').toLowerCase();
      return itemName.includes(searchLower);
    });
  };

  // useEffect para inicializar dados mestres
  useEffect(() => {
    if (equipamentos && Array.isArray(equipamentos) && equipamentos.length > 0) {
      const idsNoGrupo = buildIdsSetFromGrupo(equipamentosDoGrupo);

      let servicosRelacionados = (equipamentos || []).filter((servico) =>
        idsNoGrupo.has(getComparableId(servico))
      );
      let servicosNaoRelacionados = (equipamentos || []).filter(
        (servico) => !idsNoGrupo.has(getComparableId(servico))
      );

      // Fallback: se não encontrou nada, tenta casar por nome
      if (servicosRelacionados.length === 0 && Array.isArray(equipamentosDoGrupo) && equipamentosDoGrupo.length > 0) {
        const nomesNoGrupo = new Set((equipamentosDoGrupo || []).map((e) => String(e?.nome ?? '').toLowerCase()).filter(Boolean));
        if (nomesNoGrupo.size > 0) {
          servicosRelacionados = (equipamentos || []).filter((servico) => nomesNoGrupo.has(String(servico?.nome ?? '').toLowerCase()));
          servicosNaoRelacionados = (equipamentos || []).filter((servico) => !nomesNoGrupo.has(String(servico?.nome ?? '').toLowerCase()));
        }
      }

      setMasterLeftData(servicosNaoRelacionados);
      setMasterRightData(servicosRelacionados);
    } else {
      setMasterLeftData([]);
      setMasterRightData([]);
    }
  }, [equipamentos, equipamentosDoGrupo]);

  // Sempre enviar os dados completos (não paginados) para o componente pai
  useEffect(() => {
    setLeftTableData(masterLeftData);
  }, [masterLeftData, setLeftTableData]);

  useEffect(() => {
    setRightTableData(masterRightData);
  }, [masterRightData, setRightTableData]);

  // useEffect para filtrar e paginar dados da esquerda
  useEffect(() => {
    const filtered = filterAndPaginateTable(
      masterLeftData,
      leftTablePage,
      leftTableSearchTerm
    );
    setFilteredLeftData(filtered);
  }, [masterLeftData, leftTablePage, leftTableSearchTerm, setLeftTableData]);

  // useEffect para filtrar e paginar dados da direita
  useEffect(() => {
    const filtered = filterAndPaginateTable(
      masterRightData,
      rightTablePage,
      rightTableSearchTerm
    );
    setFilteredRightData(filtered);
  }, [masterRightData, rightTablePage, rightTableSearchTerm, setRightTableData]);

  // useEffect para limpar seleções quando dados mestres mudam
  useEffect(() => {
    setSelectedItemsLeft([]);
    setSelectedItemsRight([]);
    setSelectAllLeft(false);
    setSelectAllRight(false);
  }, [masterLeftData, masterRightData]);

  // useEffect para resetar página quando busca muda
  useEffect(() => {
    setLeftTablePage(1);
  }, [leftTableSearchTerm]);

  useEffect(() => {
    setRightTablePage(1);
  }, [rightTableSearchTerm]);

  const renderTable = (
    tableData,
    selectedItems,
    handleCheckboxChange,
    selectAll,
    page,
    setPage,
    searchTerm,
    setSearchTerm
  ) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
      {/* Header with Search */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
            {tableData === filteredLeftData
              ? "Equipamentos Disponíveis"
              : "Equipamentos do Grupo"}
          </h4>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={selectAll}
              onChange={() =>
                handleCheckboxChange(
                  tableData === filteredLeftData ? "left" : "right"
                )
              }
              className="w-4 h-4 text-vox-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-vox-blue-500 focus:ring-2"
            />
            <label className="text-sm text-gray-600 dark:text-gray-400">
              Selecionar todos
            </label>
          </div>
        </div>

        <div className="relative">
          <input
            type="text"
            placeholder="Pesquisar equipamentos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 pl-10 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-vox-blue-500 focus:border-vox-blue-500 transition-colors duration-200"
          />
          <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
          </svg>
        </div>
      </div>

      {/* Content */}
      <div className="max-h-80 overflow-y-auto">
        {tableData?.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <div className="text-gray-400 dark:text-gray-500 text-4xl mb-4">📦</div>
            <p className="text-gray-500 dark:text-gray-400">
              Nenhum equipamento encontrado
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {tableData?.map((servico) => (
              <div
                key={servico.id ?? servico.id_modelo ?? servico.nome}
                className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150 cursor-pointer"
                onClick={() => toggleSelect(servico, tableData === filteredLeftData ? "left" : "right")}
              >
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={selectedItems.some((i) => isSame(i, servico))}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() =>
                      toggleSelect(
                        servico,
                        tableData === filteredLeftData ? "left" : "right"
                      )
                    }
                    className="w-4 h-4 text-vox-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-vox-blue-500 focus:ring-2"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {servico.nome}
                    </div>
                    {servico.descricao && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {servico.descricao}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer with Pagination */}
      <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Página {page} • {tableData?.length || 0} de {
              tableData === filteredLeftData
                ? getFilteredData(masterLeftData, leftTableSearchTerm).length
                : getFilteredData(masterRightData, rightTableSearchTerm).length
            } itens {searchTerm && `(filtrados)`}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
            >
              <MdOutlineArrowBackIos size={16} />
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={tableData?.length < itemsPerPage}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
            >
              <MdOutlineArrowForwardIos size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );


  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Left Table */}
        <div className="lg:col-span-1">
          {renderTable(
            filteredLeftData,
            selectedItemsLeft,
            handleCheckboxChange,
            selectAllLeft,
            leftTablePage,
            setLeftTablePage,
            leftTableSearchTerm,
            setLeftTableSearchTerm
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col items-center justify-center space-y-4 lg:col-span-1">
          <div className="flex flex-col space-y-3">
            <button
              onClick={moveRight}
              disabled={selectedItemsLeft.length === 0}
              className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-vox-blue-500 to-vox-blue-600 hover:from-vox-blue-600 hover:to-vox-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-full transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none"
              title="Adicionar ao grupo"
            >
              <MdOutlineArrowForwardIos size={20} />
            </button>
            <button
              onClick={moveLeft}
              disabled={selectedItemsRight.length === 0}
              className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-vox-blue-500 to-vox-blue-600 hover:from-vox-blue-600 hover:to-vox-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-full transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none"
              title="Remover do grupo"
            >
              <MdOutlineArrowBackIos size={20} />
            </button>
          </div>

          {/* Counters */}
          <div className="text-center space-y-2">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Selecionados: {selectedItemsLeft.length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              No grupo: {masterRightData?.length || 0}
            </div>
          </div>
        </div>

        {/* Right Table */}
        <div className="lg:col-span-1">
          {renderTable(
            filteredRightData,
            selectedItemsRight,
            handleCheckboxChange,
            selectAllRight,
            rightTablePage,
            setRightTablePage,
            rightTableSearchTerm,
            setRightTableSearchTerm
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-orange-600">
              {selectedItemsLeft.length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Selecionados (Disponíveis)
            </div>
          </div>
          <div>
            <div className="text-2xl font-bold text-vox-blue-600">
              {masterLeftData?.length || 0}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Equipamentos Disponíveis
            </div>
          </div>
          <div>
            <div className="text-2xl font-bold text-vox-green-600">
              {masterRightData?.length || 0}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              No Grupo
            </div>
          </div>

          <div>
            <div className="text-2xl font-bold text-purple-600">
              {selectedItemsRight.length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Selecionados (Grupo)
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
